import {
    Card,
    CardContent,
    CardActions,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useStyles } from "./MachineCenterCard.styles";
import { IMachineCenterCard } from "./MachineCenterCardInterfaces";
import bottleneckIcon from "../../icons/bottle.png";
import useRolesPermissions from "hooks/useRolesPermissions";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useCallback, useEffect } from "react";
import { MachineCentersEntity } from "entities/MachineCenters/MachineCentersEntity";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckUserPasswordModal from "components/CheckUserPasswordModal";

const EMPTY_HMI_CODE = "$$EMPTY_HMI_CODE$$";

export const MachineCenterCard = ({
    machineCenter,
    handleCardOnClick,
    handleClusterEditModalOpen,
    setEditableCluster,
    handleOnClickDelete,
    handleOnClickDuplicate,
    refetch,
}: IMachineCenterCard) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [anchorElForActionMenu, setAnchorElForActionMenu] = useState<null | HTMLElement>(null);
    const [isOngoingEvent, setIsOngoingEvent] = useState(false);
    const [loadingOngoingEvent, setLoadingOngoingEvent] = useState(true);
    const [hmiCodeToRefresh, setHMICodeToRefresh] = useState<string | null>(null);
    const [hmiCodeLoading, setHmiCodeLoading] = useState<boolean>(false);

    const { validateOngoingEvent } = MachineCentersEntity.CheckMachineCenterOngoingEvent();
    const { updateMachineCenterMutation } = MachineCentersEntity.UpdateMachineCenterCorporateBO();

    const isMenuOpen = Boolean(anchorElForActionMenu);

    const handleClickMoreActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElForActionMenu(event.currentTarget);
    };

    const handleCloseMoreActionsMenu = () => {
        setAnchorElForActionMenu(null);
    };

    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const isClusterAllowed = useMemo(
        () => (machineCenter.cluster ? machineCenter.cluster.mill.cluster_enabled : false),
        [machineCenter],
    );

    const handleEditModal = useCallback(() => {
        if (machineCenter.cluster) {
            const cluster = {
                cluster_id: machineCenter.cluster!.cluster_id,
                name: machineCenter.cluster!.name,
                is_enabled: machineCenter.cluster!.is_enabled,
                cluster_code: machineCenter.cluster!.cluster_code,
                machine_centers: machineCenter.cluster!.machine_centers,
            };
            handleClusterEditModalOpen();
            setEditableCluster(cluster);
        }
    }, [handleClusterEditModalOpen, setEditableCluster, machineCenter.cluster]);

    const handleMenuOnClose = useCallback(
        (event: MouseEvent) => {
            if (event) {
                if (anchorElForActionMenu && !anchorElForActionMenu.contains(event.target as Node)) {
                    handleCloseMoreActionsMenu();
                }
            }
        },
        [anchorElForActionMenu],
    );

    const handleRefresHMICode = useCallback(async () => {
        setHmiCodeLoading(true);
        try {
            await updateMachineCenterMutation({
                variables: { machineCenterId: machineCenter.machine_center_id, changes: { hmi_code_refresh: true } },
            });
            // This timeout is necessary because the HMI code refresh operates via a database trigger.
            // Adding a delay before refetching ensures the trigger has completed its asynchronous
            // operations, preventing race conditions where we might fetch stale data. Similar to
            // debouncing, this timeout guarantees we only fetch after the backend state has stabilized.
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await refetch();
        } catch {
            setHmiCodeLoading(false);
            return;
        }
        setHMICodeToRefresh(null);
        setHmiCodeLoading(false);
    }, [updateMachineCenterMutation, machineCenter, refetch]);

    // The listener was added to replace the onClose of the Menu component,
    // The original onClose event was insufficient due to the integration of drag & drop features which
    // prevented the menu from closing with a single click outside the menu area.
    // This useEffect now attaches a mouseup event listener to close the menu when clicked outside
    useEffect(() => {
        const fetchOngoingEvent = async () => {
            setLoadingOngoingEvent(true);
            const noOngoingDowntimeEvent = await validateOngoingEvent(machineCenter.machine_center_id);
            setIsOngoingEvent(!noOngoingDowntimeEvent);
            setLoadingOngoingEvent(false);
        };

        if (isMenuOpen) {
            document.addEventListener("mouseup", handleMenuOnClose);
            fetchOngoingEvent();
        }
        return () => {
            document.removeEventListener("mouseup", handleMenuOnClose);
        };
    }, [machineCenter.machine_center_id, validateOngoingEvent, handleMenuOnClose, isMenuOpen]);

    return (
        <Card className={machineCenter.is_bottleneck ? classes.cardContainerBottleneck : classes.cardContainer}>
            {machineCenter.cluster && isClusterAllowed && (
                <Tooltip
                    placement="top"
                    PopperProps={{
                        className: classes.tooltip,
                    }}
                    title={
                        <Typography className={classes.tooltipTexts}>
                            {`${t("corporate_backoffice.cluster_view.cluster_title")} ${machineCenter.cluster.name}`}
                        </Typography>
                    }
                >
                    <GridViewOutlinedIcon
                        className={`${classes.clusterIcon}`}
                        onMouseUp={() => handleEditModal()}
                        style={{ right: machineCenter.is_bottleneck ? "30px" : "10px" }}
                    />
                </Tooltip>
            )}
            <CardContent onMouseUp={handleCardOnClick} className={classes.cardContent}>
                <Typography className={classes.shortName} gutterBottom>
                    {machineCenter.short_name}{" "}
                    <span className={classes.iconsContainer}>
                        {machineCenter.is_bottleneck && (
                            <img className={classes.bottleneckIcon} src={bottleneckIcon} alt="bottleneck_icon" />
                        )}
                    </span>
                </Typography>
                <Typography className={`${classes.name}`} gutterBottom>
                    {machineCenter.name}
                </Typography>
            </CardContent>
            <CardActions
                className={
                    CORPORATE_PERMISSIONS?.corporationPermissions?.showHmiCode
                        ? classes.cardActionsContainer
                        : classes.cardActionsContainerOnlyOne
                }
            >
                {CORPORATE_PERMISSIONS?.corporationPermissions?.showHmiCode && (
                    <div className={classes.hmiCodeContainer}>
                        <Typography
                            onMouseUp={() => {
                                if (machineCenter.hmi_code) {
                                    navigator.clipboard.writeText(machineCenter.hmi_code);
                                }
                            }}
                            variant="error"
                            className={classes.hmiCodeText}
                        >{`${t("corporate_backoffice.machine_center_details.hmi_code")}: ${
                            machineCenter.hmi_code
                                ? machineCenter.hmi_code
                                : t("corporate_backoffice.machine_center_details.no_hmi_code")
                        }`}</Typography>
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditMachineCenters &&
                            CORPORATE_PERMISSIONS?.corporationPermissions?.canRefreshHMICode && (
                                <IconButton
                                    className={classes.refreshButton}
                                    disabled={hmiCodeLoading}
                                    onMouseUp={() => setHMICodeToRefresh(machineCenter.hmi_code || EMPTY_HMI_CODE)}
                                >
                                    {hmiCodeLoading ? (
                                        <CircularProgress className={classes.loadingContainer} color="info" />
                                    ) : (
                                        <RefreshIcon className={classes.refreshIcon} />
                                    )}
                                </IconButton>
                            )}
                        <CheckUserPasswordModal
                            open={!!hmiCodeToRefresh}
                            onConfirmPassword={handleRefresHMICode}
                            onClose={() => setHMICodeToRefresh(null)}
                            title={t(
                                "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_title",
                            )}
                            question={t(
                                "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_question",
                            )}
                            questionConfirmedButtonText={t(
                                "corporate_backoffice.machine_center_details.confirm_refresh_hmi_code_modal_title",
                            )}
                        />
                    </div>
                )}
                <>
                    <IconButton
                        id={`more-actions-button-${machineCenter.machine_center_id}`}
                        aria-controls={isMenuOpen ? `more-actions-menu-${machineCenter.machine_center_id}` : undefined}
                        aria-haspopup="true"
                        aria-expanded={isMenuOpen ? "true" : undefined}
                        onMouseUp={handleClickMoreActionsMenu}
                    >
                        <MoreHorizIcon className={classes.moreIconCard} />
                    </IconButton>
                    <Menu
                        id={`more-actions-menu-${machineCenter.machine_center_id}`}
                        aria-labelledby="more-actions-button"
                        anchorEl={anchorElForActionMenu}
                        open={
                            isMenuOpen &&
                            anchorElForActionMenu?.id === `more-actions-button-${machineCenter.machine_center_id}`
                        }
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                    >
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.canDelete ? (
                            loadingOngoingEvent ? (
                                <MenuItem disabled={true}>
                                    <CircularProgress size={24} color="info" />
                                </MenuItem>
                            ) : isOngoingEvent ? (
                                <Tooltip
                                    placement="top"
                                    PopperProps={{
                                        className: classes.tooltip,
                                    }}
                                    title={
                                        <Typography className={classes.tooltipTexts}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_card.remove_tooltip",
                                            )}
                                        </Typography>
                                    }
                                >
                                    <span>
                                        <MenuItem disabled={isOngoingEvent}>
                                            {t(
                                                "corporate_backoffice.production_line_view.machine_center_card.remove_button",
                                            )}
                                        </MenuItem>
                                    </span>
                                </Tooltip>
                            ) : (
                                <MenuItem
                                    disabled={isOngoingEvent}
                                    onMouseUp={() =>
                                        handleOnClickDelete({
                                            id: machineCenter.machine_center_id,
                                            name: machineCenter.name,
                                        })
                                    }
                                >
                                    {t("corporate_backoffice.production_line_view.machine_center_card.remove_button")}
                                </MenuItem>
                            )
                        ) : (
                            <MenuItem disabled={true}>
                                {t("corporate_backoffice.production_line_view.machine_center_card.remove_button")}
                            </MenuItem>
                        )}
                        {CORPORATE_PERMISSIONS?.corporationPermissions?.canEditProductionLines && (
                            <MenuItem
                                onMouseUp={() =>
                                    handleOnClickDuplicate({
                                        machineCenterId: machineCenter.machine_center_id,
                                        name: machineCenter.name,
                                    })
                                }
                            >
                                {t("corporate_backoffice.production_line_view.machine_center_card.duplicate_button")}
                            </MenuItem>
                        )}
                    </Menu>
                </>
            </CardActions>
        </Card>
    );
};
